import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseModal from "../../Atomos/Icons/EditMode/CloseModal";
import CancelIcon from "../../Atomos/Icons/Close";
import SaveChanges from "../../Atomos/Icons/EditMode/SaveChanges";
import {
  memberModalPrimaryButtonClassName,
  memberModalSecondaryButtonClassName,
} from "./memberModalFooterButtonClasses";
import InputText from "../../staff/InputText";
import { IPerson } from "../../../interfaces/models/IPerson";
import type { PersonSocialNetwork } from "../../../interfaces/models/IPersonCreate";
import { updatePerson } from "../../../Services/Person";
import { SocialNetworksEnum, SocialNetworksType } from "../../../Enums/PersonEnums";
import {
  PERSON_SOCIAL_FIELD_DEFS,
  canonicalSocialProfileUrl,
  normalizeSocialNetworksForApi,
  type PersonSocialDisplayName,
} from "../../Organismos/Person/personForm/socialNetworks";
import { toast } from "react-toastify";

const MODAL_PAD = "px-6 md:px-8";

const SOCIAL_KEYS = Object.keys(SocialNetworksEnum) as SocialNetworksType[];

const SOCIAL_FIELDS: {
  key: SocialNetworksType;
  title: string;
  placeholder: string;
}[] = PERSON_SOCIAL_FIELD_DEFS.map((def) => {
  const key = (
    Object.entries(SocialNetworksEnum) as [SocialNetworksType, string][]
  ).find(([, value]) => value === def.name)?.[0];
  if (!key) {
    throw new Error(`No social network key for ${def.name}`);
  }
  return {
    key,
    title: def.title,
    placeholder: def.placeholder,
  };
});

function matchesNetworkKey(stored: string, key: SocialNetworksType): boolean {
  const v = SocialNetworksEnum[key];
  return (
    stored === key ||
    stored === v ||
    stored?.toLowerCase() === key.toLowerCase() ||
    stored?.toLowerCase() === v.toLowerCase()
  );
}

function findSocialUrlForKey(
  networks: { name: string; url: string }[] | undefined,
  key: SocialNetworksType
): string {
  if (!networks?.length) {
    return "";
  }
  const enumVal = SocialNetworksEnum[key];
  const hit = networks.find(
    (n) =>
      n.name === key ||
      n.name === enumVal ||
      (n.name && n.name.toLowerCase() === key.toLowerCase()) ||
      (n.name && enumVal && n.name.toLowerCase() === enumVal.toLowerCase())
  );
  return hit?.url ?? "";
}

function buildSocialNetworksPayload(
  person: IPerson,
  draft: Record<SocialNetworksType, string>
): PersonSocialNetwork[] {
  const rest = (person.socialNetworks ?? []).filter(
    (n) => !SOCIAL_KEYS.some((k) => matchesNetworkKey(n.name, k))
  );
  const preserved: PersonSocialNetwork[] = rest.map((n) => ({
    name: n.name as SocialNetworksType,
    url: n.url,
  }));
  const edited: PersonSocialNetwork[] = SOCIAL_KEYS.filter((k) => draft[k].trim()).map((k) => ({
    name: k,
    url: draft[k].trim(),
  }));
  const combined = [...preserved, ...edited];
  return normalizeSocialNetworksForApi(combined);
}

type EditSocialMediaModalProps = {
  isOpen: boolean;
  onClose: () => void;
  person: IPerson;
  onSaved: (socialNetworks: PersonSocialNetwork[]) => void;
};

const emptyDraft = (): Record<SocialNetworksType, string> => {
  const d = {} as Record<SocialNetworksType, string>;
  for (const key of SOCIAL_KEYS) {
    d[key] = "";
  }
  return d;
};

const EditSocialMediaModal: React.FC<EditSocialMediaModalProps> = ({
  isOpen,
  onClose,
  person,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Record<SocialNetworksType, string>>(emptyDraft);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<SocialNetworksType, string>>
  >({});
  const [saving, setSaving] = useState(false);

  const hydrate = useCallback(() => {
    const next = emptyDraft();
    for (const key of SOCIAL_KEYS) {
      const stored = findSocialUrlForKey(person.socialNetworks, key);
      const display = SocialNetworksEnum[key] as PersonSocialDisplayName;
      next[key] = canonicalSocialProfileUrl(stored, display) ?? stored;
    }
    setDraft(next);
    setFieldErrors({});
  }, [person]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    hydrate();
  }, [isOpen, hydrate]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const setUrl = (key: SocialNetworksType, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    if (!person.id) {
      toast.error("Missing profile id.");
      return;
    }
    const nextErrors: Partial<Record<SocialNetworksType, string>> = {};
    for (const key of SOCIAL_KEYS) {
      const value = draft[key].trim();
      if (!value) {
        continue;
      }
      const display = SocialNetworksEnum[key] as PersonSocialDisplayName;
      if (canonicalSocialProfileUrl(value, display) == null) {
        nextErrors[key] = "Enter a valid URL";
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      toast.error("Enter a valid URL for each social profile.");
      return;
    }
    setSaving(true);
    try {
      const socialNetworks = buildSocialNetworksPayload(person, draft);
      const updated = { ...person, socialNetworks };
      await updatePerson(updated);
      onSaved(socialNetworks);
      toast.success("Social media updated.");
      onClose();
    } catch {
      toast.error("Could not save social media.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const card = (
    <div
      className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-labelledby="edit-social-media-title"
    >
        <div
          className={`flex shrink-0 items-start justify-between gap-4 ${MODAL_PAD} pt-6 md:pt-8`}
        >
          <h2
            id="edit-social-media-title"
            className="font-decimal text-2xl font-semibold text-primary"
          >
            Social Media
          </h2>
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center text-gray-font hover:text-gray-600"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <CloseModal />
          </button>
        </div>

        <div
          className={`min-h-0 flex-1 overflow-y-auto ${MODAL_PAD} pb-4 pt-6 md:pt-8`}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SOCIAL_FIELDS.map(({ key, title, placeholder }) => (
              <div key={key} className="min-w-0">
                <InputText
                  title={title}
                  placeholder={placeholder}
                  value={draft[key]}
                  onChange={(v) => setUrl(key, v)}
                  error={Boolean(fieldErrors[key])}
                  errorMessage={fieldErrors[key]}
                />
              </div>
            ))}
          </div>
        </div>

        <div className={`flex flex-wrap justify-end gap-3 border-t border-gray-200 ${MODAL_PAD} py-5`}>
          <button
            type="button"
            className={memberModalSecondaryButtonClassName}
            onClick={onClose}
            disabled={saving}
          >
            <span>Cancel</span>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-font">
              <CancelIcon className="h-4 w-4" />
            </span>
          </button>
          <button
            type="button"
            className={memberModalPrimaryButtonClassName}
            onClick={handleSave}
            disabled={saving}
          >
            <span>Update Information</span>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-white [&_svg]:fill-current">
              <SaveChanges />
            </span>
          </button>
        </div>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      onClick={saving ? undefined : onClose}
      role="presentation"
    >
      {card}
    </div>,
    document.body
  );
};

export default EditSocialMediaModal;
